import React, { useState, useEffect } from 'react';
import './PayPalPayment.css';

function PayPalPayment({ onSuccess, onCancel, userEmail }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [approvalUrl, setApprovalUrl] = useState('');

  const createPayPalOrder = async () => {
    setIsProcessing(true);
    setErrorMessage('');
    
    try {
      const response = await fetch('/api/paypal/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          amount: 9.99,
          currency: 'USD'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create PayPal order');
      }

      const data = await response.json();
      console.log('✅ PayPal order created:', data);
      
      setOrderId(data.orderId);
      setApprovalUrl(data.approvalUrl);
      
      // Redirect to PayPal for payment
      window.location.href = data.approvalUrl;
      
    } catch (error) {
      setErrorMessage(error.message || 'Failed to create PayPal order');
      console.error('PayPal order creation error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const capturePayment = async (orderId) => {
    setIsProcessing(true);
    setErrorMessage('');
    
    try {
      const response = await fetch('/api/paypal/capture-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ orderId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to capture payment');
      }

      const data = await response.json();
      console.log('✅ Payment captured successfully:', data);
      
      if (data.success) {
        onSuccess(data);
      }
      
    } catch (error) {
      setErrorMessage(error.message || 'Failed to capture payment');
      console.error('PayPal capture error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Check if returning from PayPal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const payerId = urlParams.get('PayerID');
    
    if (token && payerId) {
      // User returned from PayPal, capture the payment
      capturePayment(token);
    }
  }, []);

  return (
    <div className="paypal-payment">
      <div className="payment-header">
        <h3>🚀 Upgrade to Swift Notes Pro</h3>
        <div className="payment-price">
          <span className="price-amount">$9.99</span>
          <span className="price-period">/month</span>
        </div>
      </div>

      <div className="payment-benefits">
        <h4>✨ Pro Features:</h4>
        <ul>
          <li>✅ Unlimited notes and folders</li>
          <li>✅ AI-powered note enhancement</li>
          <li>✅ Advanced search and organization</li>
          <li>✅ Priority support</li>
          <li>✅ Early access to new features</li>
        </ul>
      </div>

      <div className="payment-form-content">
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input 
            type="email" 
            id="email" 
            value={userEmail || ''} 
            readOnly 
            className="email-input" 
          />
        </div>

        {errorMessage && (
          <div className="error-message">❌ {errorMessage}</div>
        )}

        <div className="payment-actions">
          <button 
            type="button" 
            onClick={createPayPalOrder}
            disabled={isProcessing} 
            className="paypal-button"
          >
            {isProcessing ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              <>
                <img src="https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png" alt="PayPal" />
                Pay with PayPal
              </>
            )}
          </button>
          
          <button 
            type="button" 
            onClick={onCancel} 
            className="cancel-button" 
            disabled={isProcessing}
          >
            Cancel
          </button>
        </div>

        <div className="payment-security">
          <p>🔒 Secure payment powered by PayPal</p>
          <p>💳 No credit card required - use your PayPal balance</p>
          <p>🔄 Cancel anytime from your PayPal account</p>
        </div>
      </div>
    </div>
  );
}

export default PayPalPayment; 